      import React, { useState, useEffect, useRef } from 'react';
      import { X } from 'lucide-react';
      import { sendNotesToAI } from '../api/aiNotesToContent';
      
      import { createDocument, updateDocument } from '../api/workspacesApi';
      import { useNotes } from './contexts/NotesContext';
      import { useWorkspaces } from "./contexts/WorkspacesContext";
      
      
      import './AICompositionModal.css';
      import { getTagTooltip, getDomainTooltip } from './utils/tagTooltips';
      import tagTooltipsData from './config/tagTooltips.json';

    
      const getModesConfig = () => {
        return tagTooltipsData?.modes || {};
      };

      const getModesForDomain = (domain) => {
        const modesConfig = getModesConfig();
        return Object.entries(modesConfig[domain] || {});
      };

      
          
      // Simple Markdown to HTML converter
      const markdownToHtml = (markdown) => {
        if (!markdown) return '';
        
        return markdown
          // Headers
          .replace(/^### (.*$)/gim, '<h3>$1</h3>')
          .replace(/^## (.*$)/gim, '<h2>$1</h2>')
          .replace(/^# (.*$)/gim, '<h1>$1</h1>')
          // Bold
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          // Bullet points
          .replace(/^\- (.*$)/gim, '<li>$1</li>')
          .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
          // Blockquotes
          .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
          // Line breaks
          .replace(/\n\n/g, '</p><p>')
          .replace(/^(.*)$/gim, '<p>$1</p>')
          // Clean up empty paragraphs
          .replace(/<p><\/p>/g, '')
          .replace(/<p>(<h[1-6]>.*<\/h[1-6]>)<\/p>/g, '$1')
          .replace(/<p>(<ul>.*<\/ul>)<\/p>/g, '$1')
          .replace(/<p>(<blockquote>.*<\/blockquote>)<\/p>/g, '$1');
      };


      // Configuration objects (can be exposed to UI later)
      const TRAIT_CONFIG = {
        Story: { maxTraits: 4, maxCharsPerTrait: 80 },
        Programming: { maxTraits: 6, maxCharsPerTrait: 100 },
        General: { maxTraits: 5, maxCharsPerTrait: 90 }
      };

      const TOKEN_BUDGET = { 
        seedsCharsMaxTotal: 6000, 
        perSeedChars: 1200 
      };

    const REFLECTION_PRIME_DIRECTIVE = 
        `REFLECTION PRIME DIRECTIVE:
        - Begin with a one-sentence echo of the seed in plain words.
        - 1–3 short paragraphs total (120–250 words); no broad narrative.
        - Preserve the user's meaning; clarify and strengthen without adding topics.
        - Prefer concrete nouns/verbs and one specific consequence from the seed.
        - Tone: serious, humane, unsentimental. No euphemisms, no throat-clearing.`;

      const getArtTokensForDomain = (domain) => {
        return tagTooltipsData?.artTokens?.[domain] || {};
      };


      // Constraint management
      const getConstraints = (domain, hasBackground) => {
        const constraints = {
          noBioNarration: hasBackground,
          showDontTell: domain === 'Story' && hasBackground,
          capWords: true,
          avoidCliche: domain === 'Story'
        };
        return constraints;
      };

      const renderConstraintsBlock = (constraints) => {
        const lines = [];
          if (constraints.noBioNarration)
            lines.push("Do NOT narrate or paraphrase BACKGROUND; treat it as invisible guidance.");
          if (constraints.showDontTell)   
            lines.push("Reveal character traits ONLY via action, behavior, or dialogue.");
          if (constraints.avoidCliche)
            lines.push("Avoid generic abstractions and clichéd descriptions.");
          if (constraints.capWords)
            lines.push("Hard cap output at specified word count unless otherwise noted.");

        return lines.length ? "CONSTRAINTS:\n- " + lines.join("\n- ") + "\n\n" : "";
      };


        const PROMPT_EXAMPLES = {
    Story: [
      { id: 'scene_builder', label: 'Scene Builder', prompt: 'Write a scene based on the setup in #1 featuring #2 set in #3 where the conflict from #4 comes to a head.' },
      { id: 'character_dialogue', label: 'Character Dialogue', prompt: 'Create a dialogue scene between #1 and #2 inspired by the conversation style in #3 where they discuss the situation in #4 that explores the theme from #5.' },
      { id: 'character_arc', label: 'Character Arc', prompt: 'Show character growth for #1 as they pursue the goal described in #2 while facing the challenge in #3 learning the lesson from #4.' },
      { id: 'conflict_escalation', label: 'Conflict Escalation', prompt: 'Take the conflict described in #1 and escalate it to a crisis point showing how #2 and #3 each react differently within the constraints of #4 threatening the goal from #5.' },
      { id: 'plot_twist', label: 'Plot Twist', prompt: 'Introduce the twist from #1 that changes everything for #2 and reframes the conflict in #3 forcing a new approach to #4.' },
      { id: 'goal_pursuit', label: 'Goal Pursuit', prompt: 'Write a scene where #1 actively pursues the goal described in #2 despite the obstacle in #3 using the environment of #4 to their advantage.' },
      { id: 'resolution_scene', label: 'Resolution Scene', prompt: 'Write the scene where the resolution approach from #1 is attempted with #2 and #3 each playing their part revealing the deeper meaning from #4.' },
      { id: 'outline_to_scene', label: 'Outline to Scene', prompt: 'Take the structure from #1 and expand one key moment into a full dramatic scene focusing on #2\'s perspective that embodies #3.' },
      { id: 'theme_deep_dive', label: 'Theme Deep Dive', prompt: 'Write a scene that explores the theme from #1 through #2\'s experience using the situation in #3 as a vehicle with #4 reflecting the mood.' },
      { id: 'story_synthesis', label: 'Story Synthesis', prompt: 'Create a pivotal scene that brings together the characters from #1 and #2, the conflict in #3, the pursuit of #4, all within #5 while exploring #6 into one crucial moment.' },
      { id: 'setting_showcase', label: 'Setting as Character', prompt: 'Write a scene where the location #1 plays an active role in the story, affecting how #2 must act and influencing the conflict from #3 either helping or hindering #4.' },
      { id: 'premise_development', label: 'Develop Premise', prompt: 'Take the story concept from #1 and develop it into a full scene featuring #2 and #3 set in #4.' }
    ],
    Reflection: [
      { id: 'amplify_thinking', label: 'Amplify Thinking', prompt: 'Amplify the thinking from input #1 and reaction #2 without changing the core message.' },
      { id: 'structured_reasoning', label: 'Structure Reasoning', prompt: 'Organize the reasoning in #1 and #2 into clear logic.' },
      { id: 'expand_critique', label: 'Expand Critique', prompt: 'Expand the critique in #1 with focused reasoning.' },
      { id: 'counterpoint_challenge', label: 'Generate Counterpoint', prompt: 'Challenge the position in #1 with the strongest counterpoint.' },
      { id: 'connect_fragments', label: 'Connect Fragments', prompt: 'Connect the thinking across the selected notes into a coherent synthesis.' }
    ],
    General: [
      { id: 'expand_ideas', label: 'Expand Ideas', prompt: 'Take the ideas from #1 and #2 and develop them with more detail and structure.' },
      { id: 'improve_draft', label: 'Improve Draft', prompt: 'Refine and improve the draft content, making it clearer and better organized.' },
      { id: 'create_summary', label: 'Create Summary', prompt: 'Extract and summarize the key points from all selected notes.' },
      { id: 'action_plan', label: 'Action Plan', prompt: 'Convert the content into concrete, actionable steps and next actions.' },
      { id: 'follow_up_plan', label: 'Follow-up Planning', prompt: 'Suggest specific next steps and follow-up actions based on the content.' },
      { id: 'organize_notes', label: 'Organize Notes', prompt: 'Organize and structure the notes into a clear, logical format.' }
    ],
    Programming: [
      { id: 'bug_analysis', label: 'Bug Analysis', prompt: 'Analyze the bug described in #1 based on the code in #2 and requirements in #3 and suggest potential fixes.' },
      { id: 'feature_spec', label: 'Feature Spec', prompt: 'Create a detailed feature specification from the requirements in #1 and #2 considering the existing code in #3.' },
      { id: 'code_review', label: 'Code Review', prompt: 'Review the code in #1 and #2 against the requirements in #3 and provide feedback.' },
      { id: 'refactor_plan', label: 'Refactor Plan', prompt: 'Create a refactoring plan for the code in #1 to better meet the requirements in #2.' }
    ]
  };

      // Robust trait extraction
      const extractFragments = (content = "", { maxTraits, maxCharsPerTrait }) => {
        const cleaned = String(content)
          .replace(/--- AI Output ---/gi, ' ')
          .replace(/^\s*(CHARACTER PROFILE|BIOGRAPHY|BACKGROUND):\s*/i, ' ')
          .replace(/[\r\n]+/g, ' ')
          .replace(/\s{2,}/g, ' ')
          .trim();

        if (!cleaned) return [];

        const rawFrags = cleaned
          .split(/[;•·—–\-]|(?:\.\s+)/)
          .map(s => s.trim())
          .filter(Boolean);

        const fragments = [];
        for (const frag of rawFrags) {
          let f = frag.slice(0, maxCharsPerTrait);
          if (frag.length > maxCharsPerTrait) {
            const lastSpace = f.lastIndexOf(' ');
            f = (lastSpace > 20 ? f.slice(0, lastSpace) : f).trim() + '…';
          }
          
          if (!/^\b(is|was|are|were|has|had|does|did|can|could|should|would)\b/i.test(f)) {
            fragments.push(f);
          }
          if (fragments.length >= maxTraits) break;
        }

        if (!fragments.length) {
          return rawFrags.slice(0, maxTraits).map(f => f.slice(0, maxCharsPerTrait));
        }
        return fragments;
      };

      // Background rendering
      const renderBackgroundGuidance = (stickies, domain) => {
        const cfg = TRAIT_CONFIG[domain] || TRAIT_CONFIG.General;
        let out = "BACKGROUND (internal guidance only):\n";
        for (const s of stickies) {
          const frags = extractFragments(s?.content, cfg);
          const label = s?.title || 'Background';
          out += `- ${label}: ${frags.join(' • ')}\n`;
        }
        return out + "\n";
      };

      // Seed rendering with budget controls
      const truncateForSeeds = (content = "", perSeedMax) => {
        return content.length <= perSeedMax ? content
          : content.slice(0, perSeedMax - 1) + '…';
      };

    // Update function signature
      const renderSeeds = (stickies, noteTypes, perSeedMax, totalMax, domain = 'General') => {
          let total = 0;
          const heading = domain === 'Reflection'
            ? "REFLECTION SEEDS (drive the reasoning):\n"
            : domain === 'Story'
              ? "STORY SEEDS (drive the writing):\n"
              : "SEEDS:\n";
          let out = heading;
          
          // REPLACE "// ... rest stays same" WITH THIS:
          for (const s of stickies) {
            if (noteTypes[s.id] === 'background') continue;
            
            const chunk = truncateForSeeds(s?.content || "", perSeedMax);
            const header = `[Note #${s.displayNumber || '—'}: ${s.title || 'Untitled'}]` +
              (s.tag1 ? ` (${s.tag1}${s.tag2 ? `: ${s.tag2}` : ''})` : '');
            const block = `${header}\n${chunk}\n\n`;
            
            if (total + block.length > totalMax) break;
            out += block;
            total += block.length;
          }
          return out;
        };






      const AICompositionModal = ({ 
            isOpen, 
            onClose, 
            notes = [],                 // ✅ NEW NAME
            selectedNoteIds = [],       // ✅ NEW NAME
            onSendToAI,
            updateNote,                 // ✅ NEW NAME (if this prop exists)
            maxZIndex,
            onFocus
          }) => {
        // Position and size state for floating window
        const [position, setPosition] = useState({ 
          x: 650, 
          y: 350 
        });
        const [size, setSize] = useState({ width: 1000, height: 600 });
        const [isDragging, setIsDragging] = useState(false);
        const [isResizing, setIsResizing] = useState(false);
        const dragStartRef = useRef({ x: 0, y: 0 });

        // AI Interface state
        const [selectedNotes, setSelectedNotes] = useState([]);
        const [detectedDomain, setDetectedDomain] = useState('General');
        const [mode, setMode] = useState('compose');
        const [promptText, setPromptText] = useState('');
        const [aiOutput, setAiOutput] = useState("");
        const [isLoading, setIsLoading] = useState(false);
        const [noteTypes, setNoteTypes] = useState({});
        const [isStreaming, setIsStreaming] = useState(false);
        const [streamingProgress, setStreamingProgress] = useState({ tokenUsage: 0, processingTime: 0 });
        const outputContainerRef = useRef(null);
        const [selectedNoteForPreview, setSelectedNoteForPreview] = useState(null);
        const [domainManuallyChanged, setDomainManuallyChanged] = useState(false);
        const [selectedModel, setSelectedModel] = useState('gpt4');
        // DEV
        const [showPromptPreview, setShowPromptPreview] = useState(false);
        const [previewPrompt, setPreviewPrompt] = useState({ formatted: '', raw: '' });
        const [promptViewMode, setPromptViewMode] = useState('formatted'); // 'formatted' or 'raw'
        const [showExamplesModal, setShowExamplesModal] = useState(false);
      
        
        const [selectedArtToken, setSelectedArtToken] = useState(() => {
          const generalTokens = getArtTokensForDomain('General');
          return Object.keys(generalTokens)[0] || 'clear_organized';
        });
              
        // Context for creating subnotes
        const { effectiveNotePublicId, isSubnote, addNote   } = useNotes();
        const [isCreatingSubnote, setIsCreatingSubnote] = useState(false);
        const [subnoteTitle, setSubnoteTitle] = useState("AI Generated Content");
        const { createSubnote, refreshNotes, selectNote } = useNotes();

        const { 
            selectedWorkspacePublicId, 
            selectDocument 
          } = useWorkspaces();

              // Add this handler function
        const handleCreateNewSticky = async () => {
              if (!aiOutput) {
                alert("No AI output to save!");
                return;
              }
              
              if (!selectedWorkspacePublicId) {
                alert("No workspace selected!");
                return;
              }
              
              try {
                // Calculate z-index
                const topZIndex = (maxZIndex || 100) + 10;
                
                // Step 1: Create new note (returns the ID)
                const newNoteId = await addNote(topZIndex, 'regular');
                
                if (newNoteId) {
                  // Step 2: Update with AI content and auto-generated title
                  const title = "AI Note " + new Date().toLocaleTimeString();
                  await updateNote(newNoteId, {
                    content: aiOutput,
                    title: title
                  });
                  
                  console.log("✅ Created note with AI content");
                  onFocus?.(newNoteId);
                  onClose();
                }
              } catch (error) {
                console.error("Failed to create note:", error);
                alert("Failed to create note. Please try again.");
              }
            };

        // Updated useEffect for token selection when domain changes
        useEffect(() => {
          const availableTokens = getArtTokensForDomain(detectedDomain);
          const tokenKeys = Object.keys(availableTokens);
          
          if (tokenKeys.length > 0) {
            // If current token isn't available in new domain, switch to first available
            if (!availableTokens[selectedArtToken]) {
              setSelectedArtToken(tokenKeys[0]);
            }
          }
        }, [detectedDomain, selectedArtToken]);

    


          // Add this useEffect after your existing ones
          useEffect(() => {
            const availableModes = getModesForDomain(detectedDomain);
            if (availableModes.length > 0) {
              // If current mode isn't available in new domain, switch to first available
              const currentModeExists = availableModes.some(([key]) => key === mode);
              if (!currentModeExists) {
                setMode(availableModes[0][0]); // Set to first available mode
              }
            }
  }, [detectedDomain, mode]);


            // Auto-select first note for preview
        // Auto-select first note for preview
          useEffect(() => {
              const notesToUse = selectedNotes;  // ✅ RENAMED
              if (notesToUse.length > 0) {
                setSelectedNoteForPreview(notesToUse[0].id);
              } else {
                setSelectedNoteForPreview(null);
              }
            }, [selectedNotes]);  // ✅ RENAMED



        // Domain detection and auto-select first template
        // Domain detection and auto-select first template
        useEffect(() => {
            if (selectedNotes.length > 0 && !domainManuallyChanged) {  // ✅ RENAMED
              const domains = selectedNotes.map(n => n.tag1).filter(Boolean);  // ✅ RENAMED
              let topDomain = 'General';
              if (domains.length > 0) {
                const domainCounts = {};
                domains.forEach(d => domainCounts[d] = (domainCounts[d] || 0) + 1);
                topDomain = Object.keys(domainCounts).reduce((a, b) => 
                  domainCounts[a] > domainCounts[b] ? a : b, 'General');
              }
              setDetectedDomain(topDomain);
            }
          }, [selectedNotes, domainManuallyChanged]);  // ✅ RENAMED




        // Dragging handlers
        const handleMouseDown = (e) => {
          setIsDragging(true);
          dragStartRef.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
          };
        };

        const handleMouseMove = (e) => {
          if (!isDragging) return;
          setPosition({
            x: e.clientX - dragStartRef.current.x,
            y: e.clientY - dragStartRef.current.y
          });
        };

        const handleMouseUp = () => {
          setIsDragging(false);
        };

        // Resize handlers
        const handleResizeStart = (e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsResizing(true);
          
          const startX = e.clientX;
          const startY = e.clientY;
          const startWidth = size.width;
          const startHeight = size.height;

          const handleMouseMove = (moveEvent) => {
            setSize({
              width: Math.max(600, startWidth + (moveEvent.clientX - startX)),
              height: Math.max(400, startHeight + (moveEvent.clientY - startY))
            });
          };

          const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        };

        // Set up drag event listeners
        useEffect(() => {
          if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };
          }
        }, [isDragging]);

        // Original useEffect hooks
        // REPLACE Lines 70-87 WITH:
        useEffect(() => {
          if (isOpen && notes && selectedNoteIds) {  // ✅ Add isOpen check
            const selectedIdsAsStrings = selectedNoteIds.map(id => String(id));  // ✅ RENAMED
            const filtered = notes.filter(note => selectedIdsAsStrings.includes(String(note.id)));  // ✅ RENAMED
            setSelectedNotes(filtered);  // ✅ RENAMED
            
            const newNoteTypes = {};
            filtered.forEach(note => {  // ✅ RENAMED
              if (!(note.id in noteTypes)) {
                const isCharacterBio = note.tag2 === 'character' ||   // ✅ RENAMED
                                        note.content?.toLowerCase().includes('character profile') ||
                                        note.content?.toLowerCase().includes('biography');
                newNoteTypes[note.id] = isCharacterBio ? 'background' : 'seed';
              }
            });
            if (Object.keys(newNoteTypes).length > 0) {
              setNoteTypes(prev => ({ ...prev, ...newNoteTypes }));
            }
          }
        }, [isOpen, selectedNoteIds.join(',')]);  // ✅ Add isOpen, use join to avoid infinite loop



        // Handle note type toggle
        const handleNoteTypeToggle = (stickyId, newType) => {
          setNoteTypes(prev => ({
            ...prev,
            [stickyId]: newType
          }));
        };

        // Add this helper function above handleSendToAI
        const buildModeInstruction = (domain, mode) => {
          const modesConfig = getModesConfig();
          const modeConfig = modesConfig[domain]?.[mode];
          return modeConfig ? `\nMODE: ${modeConfig.instruction}` : 
            '\nMODE: Write flowing content that weaves the information together cohesively. Target ~400-600 words.';
        };

  const buildPromptPreview = () => {
    const stickiesToUse = selectedNotes;
    const backgroundNotes = stickiesToUse.filter(sticky => noteTypes[sticky.id] === 'background');
    const seedNotes = stickiesToUse.filter(sticky => noteTypes[sticky.id] !== 'background');
    
    let sections = [];

    // Domain-specific logic
    if (detectedDomain === 'Reflection' || detectedDomain === 'General') {
      sections.push({ label: 'USER PROMPT', content: promptText });
    } else {
      const constraints = getConstraints(detectedDomain, backgroundNotes.length > 0);
      const constraintsBlock = renderConstraintsBlock(constraints);
      if (constraintsBlock) {
        sections.push({ label: 'CONSTRAINTS', content: constraintsBlock });
      }
      
      if (detectedDomain === 'Reflection') {
        sections.push({ label: 'REFLECTION PRIME DIRECTIVE', content: REFLECTION_PRIME_DIRECTIVE });
      }
      
      sections.push({ label: 'USER PROMPT', content: promptText });
    }
    
    if (backgroundNotes.length > 0) {
      sections.push({ label: 'BACKGROUND', content: renderBackgroundGuidance(backgroundNotes, detectedDomain) });
      if (detectedDomain !== 'Reflection') {
        sections.push({ label: 'REMINDER', content: "Reminder: Do NOT narrate BACKGROUND; use it only to guide choices." });
      }
    }
    
    if (seedNotes.length > 0) {
      const seedsContent = renderSeeds(
        stickiesToUse.map((s, i) => ({ ...s, displayNumber: i + 1 })),
        noteTypes,
        TOKEN_BUDGET.perSeedChars,
        TOKEN_BUDGET.seedsCharsMaxTotal,
        detectedDomain
      );
      sections.push({ label: 'SEEDS', content: seedsContent });
    }



    
    sections.push({ label: 'MODE', content: buildModeInstruction(detectedDomain, mode) });
    
    // Add ART token info
    const artTokenInfo = getArtTokensForDomain(detectedDomain)[selectedArtToken];
    sections.unshift({ 
      label: '🎨 ART TOKEN (Applied by Backend)', 
      content: `${selectedArtToken}: "${artTokenInfo}"` 
    });
    
    // Build formatted view
    const formatted = sections.map(s => `=== ${s.label} ===\n${s.content}\n`).join('\n');
    
    // Build raw view
    const raw = sections.map(s => s.content).join('\n\n');
    
    setPreviewPrompt({ formatted, raw });
  };  



            
        // UPDATED CONFIG-DRIVEN FUNCTION
        const handleSendToAI = async () => {
            if (!selectedNotes.length || !promptText.trim()) {
              console.warn("No stickies selected or no prompt provided.");
              return;
            }

            setIsLoading(true);
            setIsStreaming(true);
            setAiOutput("");
            
            try {
              const stickiesToUse = selectedNotes;
              const backgroundNotes = stickiesToUse.filter(sticky => noteTypes[sticky.id] === 'background');
              const seedNotes = stickiesToUse.filter(sticky => noteTypes[sticky.id] !== 'background');
              
              // Build prompt with ART token
              let formattedPrompt;


                // DOMAIN-SPECIFIC LOGIC
                if (detectedDomain === 'Reflection' || detectedDomain === 'General') {
                  // DON'T inject ART directive here - backend will do it
                  formattedPrompt = promptText + '\n\n';
                } else {
                  // KEEP EXISTING: Full scaffolding for Story and other domains  
                  const constraints = getConstraints(detectedDomain, backgroundNotes.length > 0);
                  formattedPrompt = renderConstraintsBlock(constraints);
                  
                  if (detectedDomain === 'Reflection') {
                    formattedPrompt += REFLECTION_PRIME_DIRECTIVE + '\n\n';
                  }
                  
                  formattedPrompt += promptText + '\n\n';
                }
                  
                  // Common sections for all domains
                  if (backgroundNotes.length > 0) {
                    formattedPrompt += renderBackgroundGuidance(backgroundNotes, detectedDomain);
                    if (detectedDomain !== 'Reflection') {
                      formattedPrompt += "Reminder: Do NOT narrate BACKGROUND; use it only to guide choices.\n\n";
                    }
                  }
                  
                  if (seedNotes.length > 0) {
                    formattedPrompt += renderSeeds(
                      stickiesToUse.map((s, i) => ({ ...s, displayNumber: i + 1 })),
                      noteTypes,
                      TOKEN_BUDGET.perSeedChars,
                      TOKEN_BUDGET.seedsCharsMaxTotal,
                      detectedDomain
                    );
                  }
                  
                  formattedPrompt += buildModeInstruction(detectedDomain, mode);
                  
    

              console.log("=== COMPLETE PROMPT SENT TO AI ===");
              console.log(formattedPrompt);
              console.log("=== END PROMPT ===");

          // STREAMING CALLBACKS
          const onChunk = (chunkData) => {
            // Update the output in real-time as chunks arrive
            setAiOutput(chunkData.accumulatedContent);
            setStreamingProgress({
              tokenUsage: chunkData.tokenUsage,
              processingTime: chunkData.processingTime
            });
          };

          const onComplete = (finalData) => {
            console.log("Streaming completed:", finalData);
            setAiOutput(finalData.content);
            setStreamingProgress({
              tokenUsage: finalData.tokenUsage,
              processingTime: finalData.processingTime
            });
            setIsStreaming(false);
            
          };

          const onError = (error) => {
            console.error("Streaming error:", error);
            setAiOutput('Error: ' + error.message);
            setIsStreaming(false);
          };

          // Call the streaming API
          // For Reflection domain, send ART token; for others, send null
          // Send ART token for all domains that support it
          const artTokenToSend = selectedArtToken;
          console.log("=== FRONTEND DEBUG ===");
          console.log("detectedDomain:", detectedDomain);
          console.log("selectedArtToken:", selectedArtToken);
          console.log("artTokenToSend:", artTokenToSend);
          console.log("=== END FRONTEND DEBUG ===");
          await sendNotesToAI(
            stickiesToUse.map((s, index) => ({ 
              id: s.id, 
              content: s.content, 
              type: s.tag2 || s.type || 'note',
              displayNumber: index + 1,
              noteType: noteTypes[s.id] || 'seed'
            })), 
            formattedPrompt, // This will be the raw prompt without ART directive
            artTokenToSend,   // NEW: Send the ART token key
            selectedModel,
            onChunk,
            onComplete,
            onError
          );

        } catch (error) {
          console.error("Failed to process AI request:", error);
          setAiOutput('Error: ' + error.message);
          setIsStreaming(false);
        } finally {
          setIsLoading(false);
        }
      };

  const handleCreateDocument = async () => {
    if (!aiOutput) {
      alert("No AI output to save!");
      return;
    }
    

    
    if (!selectedWorkspacePublicId) {
      alert("No workspace selected!");
      return;
    }
    
    setIsCreatingSubnote(true);
    
    try {
      // Get title from user
      const title = prompt("Enter title for document:", "AI Generated Content");
      if (!title) {
        setIsCreatingSubnote(false);
        return; // User cancelled
      }
      
      // Create new document in current workspace
      const newDocument = await createDocument(selectedWorkspacePublicId);
      
      if (newDocument && newDocument.public_id) {
        // Format the AI output as HTML
        const paragraphs = aiOutput.split('\n\n');
        const formattedContent = '<p>' + paragraphs.join('</p><p>&nbsp;</p><p>') + '</p>';
        const finalContent = formattedContent
          .replace(/<p><\/p>/g, '')
          .replace(/<p>&nbsp;<\/p>$/, '');
        
        // Update the document with title and content
        await updateDocument(newDocument.public_id, {
          title: title,
          content: finalContent
        });
        
        // Select the new document
        selectDocument(newDocument.public_id);
        
        onClose();
      }
    } catch (error) {
      console.error("Failed to create document:", error);
      alert("Failed to create document. Please try again.");
    } finally {
      setIsCreatingSubnote(false);
    }
  };

        if (!isOpen) return null;



        const effectiveStickies = selectedNotes;


        return (
          <>
          <div
            className="ai-composition-modal-container"
            style={{
              left: position.x,
              top: position.y,
              width: size.width,
              height: size.height
            }}
          >
            {/* Header/Drag Bar */}
            <div className="ai-composition-header" onMouseDown={handleMouseDown}>
              <h2>Composer</h2>
              <button className="ai-composition-close-btn" onClick={onClose}>
                <X size={20} />
              </button>
            </div>

            {/* NEW: Toolbar/Menu Bar */}
            <div className="ai-composition-toolbar">
              {/* Domain Section */}
              <div className="toolbar-section">
                <label className="toolbar-label">Model:</label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => setSelectedModel('gpt4')}
                        className={`model-pill ${selectedModel === 'gpt4' ? 'model-pill-active' : ''}`}
                      >
                        GPT-4
                      </button>
                      <button
                        onClick={() => setSelectedModel('claude')}
                        className={`model-pill ${selectedModel === 'claude' ? 'model-pill-active' : ''}`}
                      >
                        Claude
                      </button>
                    </div>
                <label className="toolbar-label">Domain:</label>
                <select 
                  value={detectedDomain}
                      onChange={(e) => {
                      const newDomain = e.target.value;
                      setDetectedDomain(newDomain);
                      setDomainManuallyChanged(true);
                    }}
                  className="toolbar-dropdown"
                  title={getDomainTooltip(detectedDomain)}
                >
                  <option value="General">General</option>
                  <option value="Story">Story</option>
                  <option value="Reflection">Reflection</option>
                </select>
              </div>

              {/* NEW: ART Token Dropdown */}
          <div className="toolbar-section">
    <label className="toolbar-label">Voice:</label>
    <select 
      value={selectedArtToken}
      onChange={(e) => setSelectedArtToken(e.target.value)}
      className="toolbar-dropdown"
      title={`Writing style: ${getArtTokensForDomain(detectedDomain)[selectedArtToken]}`}  // ADD THIS
    >
      {Object.entries(getArtTokensForDomain(detectedDomain)).map(([key, label]) => (
        <option key={key} value={key}>{label}</option>
      ))}
    </select>
  </div>

              {/* Mode Toggle */}
              {/* Mode Dropdown */}
              <div className="toolbar-section">
                <label className="toolbar-label">Mode:</label>
                <select 
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="toolbar-dropdown"
                  title={getModesConfig()[detectedDomain]?.[mode]?.instruction || ''} 
                >
                  {getModesForDomain(detectedDomain).map(([key, modeConfig]) => (
                    <option key={key} value={key}>{modeConfig.label}</option>
                  ))}
                </select>
              </div>

              {/* Process Button */}
        {process.env.REACT_APP_ENVIRONMENT === 'development' && (
              <button
                onClick={() => {
                  buildPromptPreview();
                  setShowPromptPreview(true);
                }}
                disabled={!promptText.trim() || selectedNotes.length === 0}
                className="process-btn"
                style={{ backgroundColor: '#6b7280', marginRight: '8px' }}
              >
                👁️ View Prompt
              </button>
            )}
              <button
                  onClick={handleSendToAI}
                  disabled={!promptText.trim() || selectedNotes.length === 0 || isLoading}
                  className="process-btn"
                >
                  {isStreaming ? 
                    `Streaming... (${streamingProgress.tokenUsage} tokens)` : 
                    isLoading ? 'Processing...' : 
                    'Process'
                  }
                </button>

            </div>

            {/* Content */}
            <div className="ai-composition-body">
              {/* Selected Stickies Summary with Background/Seed toggles */}
              {effectiveStickies.length > 0 && (
                <div className="selected-stickies-summary">
                  <strong>Selected Notes ({effectiveStickies.length}):</strong>

                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {effectiveStickies.map((sticky, index) => {
                    const currentType = noteTypes[sticky.id] || 'seed';
                    const isSelected = selectedNoteForPreview === sticky.id;
                    return (
                      <div 
                        key={sticky.id} 
                        onClick={() => setSelectedNoteForPreview(sticky.id)}
    className={`note-item ${currentType === 'background' ? 'note-item-background' : 'note-item-seed'} ${isSelected ? 'note-item-selected' : ''}`}
  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                        <span className="sticky-reference">
                        <strong>#{index + 1}: {sticky.title || 'Untitled'}</strong>
                        {sticky.tag1 && !domainManuallyChanged && (
                          <span className="sticky-tag"> ({sticky.tag1}{sticky.tag2 ? `: ${sticky.tag2}` : ''})</span>
                        )}
                        {domainManuallyChanged && (
                          <span className="sticky-tag"> (processed as {detectedDomain})</span>
                        )}
                      </span>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNoteTypeToggle(sticky.id, 'background');
                              }}
                              className={`type-toggle-btn ${currentType === 'background' ? 'type-toggle-btn-background' : 'type-toggle-btn-inactive'}`}
                              title="Internal guidance only - not narrated in output. Use for context like character bios, world-building, or reference material that informs the writing without being explicitly discussed."
                            >
                              Background
                            </button>
                            <button
                              onClick={() => handleNoteTypeToggle(sticky.id, 'seed')}
                              className={`type-toggle-btn ${currentType === 'seed' ? 'type-toggle-btn-seed' : 'type-toggle-btn-inactive'}`}
                              title="Active content to develop. This material will be directly referenced and incorporated into the generated output."
                            >
                              Seed
                            </button>
                          </div>


                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Prompt Text Area */}
              <div className="prompt-container">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ margin: 0 }}>Your Prompt:</label>
                    <button
                      onClick={() => setShowExamplesModal(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '12px',
                        padding: '2px 6px',
                        textDecoration: 'underline',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#2c5282'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    >
                      💡 See examples
                    </button>
                  </div>
                  <textarea 
                    value={promptText}
                    onChange={(e) => {
                      setPromptText(e.target.value);
                    }}
                    className="prompt-textarea"
                    placeholder="Reference your notes by number: 'Combine #1 and #3' or 'Use #2 as the setting for #4'..."
                  />



                  {/* Note Preview Area */}
                    {selectedNoteForPreview && effectiveStickies.length > 0 && (
                      <div className="prompt-container">
                        <label>
                          Note Preview - {effectiveStickies.find(s => s.id === selectedNoteForPreview)?.title || 'Untitled'}:
                        </label>
                        <textarea 
                          value={effectiveStickies.find(s => s.id === selectedNoteForPreview)?.content || ''}
                          readOnly
                          className="note-preview-textarea"
                          placeholder="Note content will appear here..."
                        />
                      </div>
                    )}
                </div>


              {/* ADD THE STREAMING INDICATOR RIGHT HERE */}
              {isStreaming && (
    <div className="streaming-indicator">
    <div className="streaming-spinner"></div>
                  Streaming response... {streamingProgress.tokenUsage} tokens • {streamingProgress.processingTime.toFixed(1)}s
                </div>
              )}
              <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

              {/* AI Output Section */}
              {/* AI Output Section */}
              {/* AI Output Section - FULL SCREEN MODE */}
      {(aiOutput || isStreaming) && (
        <>
          {/* Hide everything else when there's output */}
          <style>{`
            .selected-stickies-summary,
            .prompt-container,
            .ai-composition-toolbar {
              display: none !important;
            }
            .ai-composition-body {
              padding: 0 !important;
            }
          `}</style>
          
          <div 
          ref={outputContainerRef} 
          style={{
            position: 'absolute',
            top: '86px', // Below header (44px) + toolbar (42px)
            left: '0',
            right: '0', 
            bottom: '60px', // Above action buttons
            padding: '20px',
            backgroundColor: 'white',
            overflow: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #ccc',
              paddingBottom: '10px'
            }}>
              <h3 style={{ margin: 0, fontSize: '20px' }}>Generated Content</h3>
              
            </div>
            
          <div 
                className="ai-output-content"
                style={{
                  fontSize: '18px',
                  lineHeight: '1.8',
                  color: '#333',
                  fontFamily: 'Georgia, serif'
                }}
                dangerouslySetInnerHTML={{ __html: markdownToHtml(aiOutput) }}
              />
          </div>
        </>
      )}


              {/* Action buttons */}
              {/* Action buttons */}
                <div className="ai-composition-actions" style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '15px'
                }}>

                  
                  {aiOutput && (
                    <button 
                      className="ai-composition-create-subnote-btn"  // Use same class
                      onClick={() => setAiOutput('')} 
                    >
                      ← Back to Edit
                    </button>
                    
                  )}
                  
                  {aiOutput && (
                    <button 
                      className="ai-composition-create-subnote-btn"
                      onClick={handleCreateDocument} 
                      disabled={isSubnote || isCreatingSubnote}
                    >
                      {isCreatingSubnote ? "Creating..." : "Create Document"}
                    </button>
                  )}

                  
                      {aiOutput && (
                        <button 
                          className="ai-composition-create-subnote-btn"
                          onClick={handleCreateNewSticky}
                        >
                          Create New Note
                        </button>
                      )}
                  
                  <button 
                    className="ai-composition-close-btn"
                    onClick={onClose}
                    disabled={isCreatingSubnote}
                  >
                    Close
                  </button>
                </div>
            </div>

            {/* Resize Handle */}
            <div 
              className="ai-composition-resize-handle" 
              onMouseDown={handleResizeStart}
            />
        
          </div>
  {/* Prompt Preview Modal */}
  {showPromptPreview && (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 999999999000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '900px',
        maxHeight: '80vh',
        width: '90%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '12px'
        }}>
          <h3 style={{ margin: 0, fontSize: '20px' }}>Prompt Preview</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setPromptViewMode('formatted')}
              style={{
                padding: '6px 12px',
                backgroundColor: promptViewMode === 'formatted' ? '#2c5282' : '#e5e7eb',
                color: promptViewMode === 'formatted' ? 'white' : '#374151',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              📋 Formatted
            </button>
            <button
              onClick={() => setPromptViewMode('raw')}
              style={{
                padding: '6px 12px',
                backgroundColor: promptViewMode === 'raw' ? '#2c5282' : '#e5e7eb',
                color: promptViewMode === 'raw' ? 'white' : '#374151',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              📄 Raw
            </button>
            <button
              onClick={() => setShowPromptPreview(false)}
              style={{
                padding: '6px 12px',
                backgroundColor: '#e5e7eb',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              ✕
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#f9fafb',
          padding: '16px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '13px',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
          border: '1px solid #e5e7eb'
        }}>
          {promptViewMode === 'formatted' ? previewPrompt.formatted : previewPrompt.raw}
        </div>
        
        {/* Footer Info */}
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#fef3c7',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#92400e'
        }}>
          ℹ️ <strong>Note:</strong> The ART token directive will be prepended by the backend before sending to the AI model.
        </div>
      </div>
    </div>
  )}
  {/* Examples Modal */}
  {showExamplesModal && (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 999999999001,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '700px',
        maxHeight: '80vh',
        width: '90%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '12px'
        }}>
          <h3 style={{ margin: 0, fontSize: '20px' }}>💡 Prompt Examples - {detectedDomain}</h3>
          <button
            onClick={() => setShowExamplesModal(false)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#e5e7eb',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {PROMPT_EXAMPLES[detectedDomain]?.map((example) => (
            <div key={example.id} style={{
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '8px',
                fontSize: '14px',
                color: '#374151'
              }}>
                {example.label}
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                marginBottom: '12px',
                lineHeight: '1.5'
              }}>
                "{example.prompt}"
              </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
    <button
      onClick={() => {
        setPromptText(example.prompt);
        setShowExamplesModal(false);
      }}
      style={{
        padding: '6px 12px',
        backgroundColor: '#2c5282',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500'
      }}
    >
      ➕ Use This
    </button>
  </div>
            </div>
          ))}
        </div>
        
        {/* Footer */}
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#eff6ff',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#1e40af'
        }}>
          💡 <strong>Tip:</strong> Reference your selected notes using #1, #2, #3, etc.
        </div>
      </div>
    </div>
  )}
          </>
        );

        
      };

      export default AICompositionModal;